/**
 * ServicesList — a clean catalog-style list of services.
 * Ideal for businesses with many services or variable pricing.
 *
 * Registered as: services / services-list
 */
import React from "react";
import type { SectionComponentProps } from "../../registry.js";

interface ServiceItem {
  id?: string;
  name: string;
  description?: string;
  priceLabel?: string;   // e.g. "from $45", "call for pricing"
  duration?: string;     // e.g. "~2 hrs"
  category?: string;
}

interface ServicesListProps {
  headline?: string;
  subheadline?: string;
  services?: ServiceItem[];
  ctaLabel?: string;
  ctaTarget?: string;
}

const DEFAULT_SERVICES: ServiceItem[] = [
  { name: "Weekly Lawn Mowing", description: "Regular mowing, edging & blowout. Keeps your lawn looking sharp all season.", priceLabel: "from $45", duration: "~45 min", category: "Maintenance" },
  { name: "Biweekly Mowing", description: "Same great service, every two weeks.", priceLabel: "from $55", duration: "~45 min", category: "Maintenance" },
  { name: "Spring Cleanup", description: "Full leaf removal, bed edging & first mow of the season.", priceLabel: "from $149", duration: "~3 hrs", category: "Seasonal" },
  { name: "Fall Cleanup", description: "Leaf removal, final trim & winterisation prep.", priceLabel: "from $149", duration: "~3 hrs", category: "Seasonal" },
  { name: "Fertilisation", description: "Seasonal fertiliser application for lush, healthy grass.", priceLabel: "from $79", duration: "~1 hr", category: "Treatments" },
  { name: "Aeration", description: "Core aeration to relieve compaction and improve root growth.", priceLabel: "from $99", duration: "~1.5 hrs", category: "Treatments" },
];

export function ServicesList({ props }: SectionComponentProps) {
  const {
    headline = "Services & Pricing",
    subheadline,
    services = DEFAULT_SERVICES,
    ctaLabel = "Get a Quote",
    ctaTarget = "#quote",
  } = props as ServicesListProps;

  // Group by category
  const grouped = services.reduce<Record<string, ServiceItem[]>>((acc, svc) => {
    const cat = svc.category ?? "Services";
    if (!acc[cat]) acc[cat] = [];
    acc[cat]!.push(svc);
    return acc;
  }, {});

  const categories = Object.keys(grouped);
  const isGrouped = categories.length > 1 || (categories.length === 1 && categories[0] !== "Services");

  return (
    <section className="py-20 px-6 bg-muted/20" id="services">
      <div className="max-w-3xl mx-auto">
        {(headline || subheadline) && (
          <div className="mb-10">
            {headline && (
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{headline}</h2>
            )}
            {subheadline && (
              <p className="text-muted-foreground text-base leading-relaxed">{subheadline}</p>
            )}
          </div>
        )}

        <div className="space-y-8">
          {(isGrouped ? categories : [""]).map((cat) => {
            const items = isGrouped ? grouped[cat]! : services;
            return (
              <div key={cat || "all"}>
                {isGrouped && (
                  <h3 className="text-xs font-bold uppercase tracking-widest text-primary mb-4">{cat}</h3>
                )}
                <div className="space-y-3">
                  {items.map((svc, i) => (
                    <div
                      key={svc.id ?? svc.name ?? i}
                      data-stagger-child
                      className="bg-background rounded-xl border border-border p-5 flex flex-col sm:flex-row sm:items-start gap-4"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3 flex-wrap">
                          <h4 className="font-semibold text-foreground text-base leading-tight">{svc.name}</h4>
                          {svc.duration && (
                            <span className="text-xs text-muted-foreground bg-muted/60 px-2.5 py-1 rounded-full mt-0.5">
                              {svc.duration}
                            </span>
                          )}
                        </div>
                        {svc.description && (
                          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{svc.description}</p>
                        )}
                      </div>
                      {svc.priceLabel && (
                        <div className="sm:text-right flex-shrink-0">
                          <span className="font-bold text-foreground tabular-nums text-base">{svc.priceLabel}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {ctaLabel && ctaTarget && (
          <div className="mt-10 text-center">
            <a
              href={ctaTarget}
              className="inline-flex items-center rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              {ctaLabel}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
